import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import FormHelperText from '@mui/material/FormHelperText'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import RadioGroup from '@mui/material/RadioGroup'
import Radio from '@mui/material/Radio'
import DeleteIcon from '@mui/icons-material/Delete'
import LockIcon from '@mui/icons-material/Lock'
import React, { useEffect, useMemo, useState } from 'react'
import { ISalesTeam, ISalesTeamPosition } from '@user/store/salesTeam/typings'
import { useSelector } from 'react-redux'
import { selectSalesTeamById } from '@user/store/salesTeam'
import { IFormikUserModel } from '../typings'
import { Field, useFormikContext } from 'formik'
import { StoreData } from '@user/store/reducers/typings'
import { LightTooltip, InfoTooltip } from '@sales-center/components'
import Box from '@mui/material/Box'

type Props = {
  onDeleteClick: () => void
  salesTeamResourcesCountIds: number[] | undefined
  index: number
  salesTeams: ISalesTeam[]
}

export default function SalesTeamAssignmentCard(props: Props) {
  const { onDeleteClick, salesTeamResourcesCountIds } = props

  const [disableDelete, setDisableDelete] = useState(false)

  const {
    handleChange,
    values: { isActive, salesTeamAssignments },
    errors,
    submitCount,
  } = useFormikContext<IFormikUserModel>()

  useEffect(() => {
    if (salesTeamResourcesCountIds)
      setDisableDelete(
        salesTeamResourcesCountIds.find(
          (x) => x == +salesTeamAssignments[props.index].salesTeamId,
        ) != undefined,
      )
  }, [salesTeamResourcesCountIds, props.index, salesTeamAssignments])

  const selectedSalesTeam: ISalesTeam | undefined = useSelector((state) =>
    selectSalesTeamById(
      state as StoreData,
      salesTeamAssignments[props.index].salesTeamId,
    ),
  )
  const selectedSalesTeamPositions = useMemo(() => {
    return selectedSalesTeam
      ? selectedSalesTeam.positions
          .filter((x) => !x.systemCreated)
          .sort((a, b) => a.title.localeCompare(b.title))
      : []
  }, [selectedSalesTeam])

  const error =
    submitCount > 0 &&
    errors[`salesTeamAssignments[${props.index}].salesTeamPositionId`]

  return (
    <Paper variant="outlined" sx={{ width: '100%', overflow: 'hidden' }}>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#F3F5F7',
          height: (theme) => theme.spacing(4.7),
          paddingLeft: 1.6,
          paddingRight: 1,
        }}
      >
        <Typography variant="subtitle3">{selectedSalesTeam?.name}</Typography>
        {disableDelete ? (
          <LightTooltip
            title={`You must re-assign any open records owned by this user before you can remove them from the sales team.`}
          >
            <IconButton disableRipple>
              <LockIcon
                fontSize="small"
                sx={{ color: (theme) => theme.palette.action.active }}
              />
            </IconButton>
          </LightTooltip>
        ) : (
          <IconButton disabled={!isActive} onClick={onDeleteClick}>
            <DeleteIcon
              fontSize="small"
              sx={{ color: (theme) => theme.palette.action.active }}
            />
          </IconButton>
        )}
      </Stack>
      <Stack paddingLeft={2} paddingRight={1} my={1.1}>
        {selectedSalesTeam &&
          (selectedSalesTeam.positions &&
          selectedSalesTeam.positions.length > 0 ? (
            <Stack>
              <Box alignItems={'center'} flexDirection={'row'} display={'flex'}>
                <Typography
                  sx={{
                    color: (theme) =>
                      error
                        ? theme.palette.error.main
                        : theme.palette.text.secondary,
                  }}
                  variant="body1"
                >
                  Sales Team Position / Access*
                </Typography>
                <InfoTooltip
                  infoText={
                    'Users added to this position will have access to view and manage records owned by users beneath them.'
                  }
                  tooltipPlacement="right"
                  sx={{
                    fontSize: '18px',
                    verticalAlign: 'middle',
                    pointerEvents: 'all',
                    mb: 0.2,
                    opacity: 1,
                    color: (theme) => theme.palette.action.active,
                    ml: 0.3,
                  }}
                />
              </Box>

              <FormControl component="fieldset">
                <Field
                  name={`salesTeamAssignments[${props.index}].salesTeamPositionId`}
                  validate={(value) =>
                    !!value && value.length === 0
                      ? 'Please select a Sales Team Position'
                      : undefined
                  }
                >
                  {({ field }) => (
                    <RadioGroup
                      aria-label="sales team positions"
                      sx={{ pl: 1.5 }}
                      name={field.name}
                      ref={field.ref}
                      onChange={handleChange}
                      value={
                        salesTeamAssignments[props.index].salesTeamPositionId
                      }
                    >
                      {selectedSalesTeamPositions.map(
                        (position: ISalesTeamPosition, index: number) => (
                          <FormControlLabel
                            sx={{ mb: 0 }}
                            key={index}
                            value={position.id?.toString()}
                            control={<Radio disabled={!isActive} />}
                            label={position.title}
                          />
                        ),
                      )}
                    </RadioGroup>
                  )}
                </Field>
              </FormControl>
              {error && (
                <FormHelperText error={error}>Required*</FormHelperText>
              )}
            </Stack>
          ) : (
            <Typography variant="body1">
              Sales Team has no positions. Select a Sales Team with a position,
              or add positions to this Sales Team.
            </Typography>
          ))}
      </Stack>
    </Paper>
  )
}
